import * as R from 'ramda'
import {
  ChangeHallInfo,
  ChangeHallOwner,
  ChangeHallPermission,
  CheckIdentifier,
  CreateHall,
  CreateHallOptions,
  GetHallByIdentifier,
  GetHallByInviteLink,
  Hall,
  HallEditableKeys,
  MakeHallPrivate,
  MakeHallPublic,
  RemoveHall,
  reqChangeHallInfo,
  reqChangeHallOwner,
  reqChangeHallPermission,
  reqCheckIdentifier,
  reqCreateHall,
  reqGetHallByIdentifier,
  reqGetHallByInviteLink,
  reqMakeHallPrivate,
  reqMakeHallPublic,
  reqRemoveHall,
  reqSearchHall,
  SearchHall,
} from '@weblite/ts-action/hall'
import { GroupMemberChangeablePermissions } from '@weblite/ts-action/subscription'
import { User } from '@weblite/ts-action/user'
import { decodeHallPermission } from '@weblite/permission'
import { request } from '$setup/ws'
import { nonClientRoomsStorage } from '$logic/room/storage'
import {
  showChatRemovedSnackbar,
  showInvalidLinkSnackbar,
  showInviteLinkExpiredSnackbar,
} from '$comp/snackbars'
import { usersStorage } from '$logic/users/storage'
import { getUser, getUserByUsername } from '$logic/users'
import * as roomService from '../room'
import { getRoom, isHall } from '../room'
import * as subscriptionService from '../subscription'
import { decodeSubscriptionPermissions, getSubscription } from '../subscription'
import { hallsStorage } from './storage'
import { MOOKEB_TAG } from './constant'
import {
  getUserSessionsLimitFromTag,
  getUserSessionsLimitTag,
  USER_SESSIONS_LIMIT_TAG_PREFIX,
} from './tags'
import { getActiveSessions } from '$logic/auth'
import { deleteWorkspaceIfHasNoOtherRooms } from '$logic/workspace'

export const getHall = hallsStorage.get

export const getAllHall = hallsStorage.getAll

export const loadAndGetAllHall = hallsStorage.dbGetAllHalls

export async function isPublicHall(roomId: string) {
  const room = await getRoom(roomId)

  return room?.type && isHall(room.type) && (await getHall(roomId))?.public
}

export async function getGroupChangeablePermission(
  roomId: string,
): Promise<GroupMemberChangeablePermissions> {
  const hall = await getHall(roomId)
  const allPermissions = decodeHallPermission(hall?.permissions)
  return R.pick(
    [
      'addMembers',
      'changeHallInfo',
      'sendMedia',
      'sendMessage',
      'sendWapp',
      'pinMessage',
    ],
    allPermissions,
  )
}

export async function changeGroupPermissions(
  roomId: string,
  permissions: GroupMemberChangeablePermissions,
): Promise<boolean> {
  const res = await request<ChangeHallPermission>(
    reqChangeHallPermission({ roomId, permissions }),
  )
  if (res.error) return false
  await hallsStorage.set(roomId, res.payload)
  return true
}

export async function changeHallInfo(
  roomId: string,
  info: Partial<Pick<Hall, HallEditableKeys>>,
): Promise<boolean> {
  const res = await request<ChangeHallInfo>(reqChangeHallInfo({ roomId, info }))
  if (res.error) {
    if (res.error.code === 'not-hall-subscriber') return false
    if (res.error.code === 'subscriber-has-not-access') return false
    return false
  }
  await hallsStorage.set(roomId, { ...info, ...res.payload })
  return true
}

export async function checkIdentifier(identifier: string): Promise<boolean> {
  const res = await request<CheckIdentifier>(reqCheckIdentifier({ identifier }))
  if (res.error) {
    if (res.error.code === 'invalid-identifier') return false
    if (res.error.code === 'repetitive-identifier') return false
    return false
  }
  return true
}

export async function makeHallPublic(
  roomId: string,
  identifier: string,
): Promise<boolean> {
  const res = await request<MakeHallPublic>(
    reqMakeHallPublic({ roomId, identifier }),
  )
  if (res.error) {
    if (res.error.code === 'invalid-identifier') return false
    if (res.error.code === 'repetitive-identifier') return false
    if (res.error.code === 'subscriber-has-not-access') return false
    return false
  }
  await hallsStorage.set(roomId, {
    public: true,
    identifier,
    updatedAt: res.payload.updatedAt,
  })
  return true
}

export async function makeHallPrivate(roomId: string): Promise<boolean> {
  const res = await request<MakeHallPrivate>(reqMakeHallPrivate({ roomId }))
  if (res.error) {
    if (res.error.code === 'subscriber-has-not-access') return false
    return false
  }
  await hallsStorage.set(roomId, {
    public: false,
    identifier: undefined,
    updatedAt: res.payload.updatedAt,
  })
  return true
}

export async function changeHallOwner(
  roomId: string,
  ownerId: string,
): Promise<boolean> {
  const res = await request<ChangeHallOwner>(
    reqChangeHallOwner({ roomId, ownerId }),
  )
  if (res.error) {
    if (res.error.code === 'subscriber-has-not-access') {
      // NOTE: Client user is not hall owner
      return false
    }
    if (res.error.code === 'not-hall-subscriber') {
      // NOTE: Selected user is not hall subscriber
      return false
    }
    if (res.error.code === 'chosen-user-not-permitted') {
      // NOTE: Selected user is not hall admin
      return false
    }
    return false
  }
  return true
}

export async function removeHall(
  roomId: string,
  deleteForAllMembers: boolean,
  disableSnackbar = false,
): Promise<boolean> {
  const room = await getRoom(roomId)
  const res = await request<RemoveHall>(
    reqRemoveHall({ roomId, deleteForAllMembers }),
  )
  if (res.error) {
    if (res.error.code === 'subscriber-has-not-access') return false
    return false
  }
  await roomService.pruneRoom(roomId)
  await deleteWorkspaceIfHasNoOtherRooms(room!)
  if (!disableSnackbar) showChatRemovedSnackbar()

  return true
}

export async function getHallByInviteLink(link: string) {
  const res = await request<GetHallByInviteLink>(
    reqGetHallByInviteLink({ link }),
    { retry: -1 },
  )
  if (res.error) {
    // NOTE: res.error.code = 'revoked-link' | 'expired-link' | 'not-found-link' | 'user-has-not-access'
    if (res.error.code === 'expired-link') showInviteLinkExpiredSnackbar()
    else showInvalidLinkSnackbar()
    return null
  }
  nonClientRoomsStorage.add(res.payload.room)
  await addHall(res.payload.hall)
  return res.payload
}

export async function searchHall(
  query: string,
): Promise<{ halls: Hall[]; userHalls: Hall[] } | undefined> {
  const res = await request<SearchHall>(reqSearchHall({ query }))
  if (res.error) {
    if (res.error.code === 'invalid-query') return undefined
    return undefined
  }
  const { halls, userHalls } = res.payload
  await hallsStorage.add(userHalls)
  await hallsStorage.add(halls)
  return res.payload
}

export async function createHall(
  createHallOptions: CreateHallOptions,
): Promise<false | string> {
  const res = await request<CreateHall>(
    reqCreateHall({ options: createHallOptions }),
  )
  if (res.error) {
    console.warn(res.error)
    return false
  }
  await subscriptionService.addHallDependencies(res.payload)
  return res.payload.room.id
}

export async function addHall(hall: Hall) {
  await hallsStorage.add(hall)
}

export async function getHallByIdentifier(identifier: string) {
  const hall = await hallsStorage.findByIdentifier(identifier)

  // NOTE: If user is not member of hall, should fetch from server to make sure that data is valid
  if (hall && (await getSubscription(hall.roomId)))
    // NOTE: Use getHall To load data in redux
    return getHall(hall.roomId)

  const res = await request<GetHallByIdentifier>(
    reqGetHallByIdentifier({ identifier }),
    { retry: -1 },
  )
  if (!res.payload?.hall) return null

  nonClientRoomsStorage.add(res.payload.room)
  await addHall(res.payload.hall)
  return res.payload.hall
}

export async function findHallOrUserByIdentifier(
  identifier: string,
): Promise<['user', User] | ['hall', Hall] | undefined> {
  const [localHall, localUser] = await Promise.all([
    hallsStorage.findByIdentifier(identifier),
    usersStorage.findByUsername(identifier),
  ])
  if (localUser) return ['user', await getUser(localUser.id)]
  if (localHall) return ['hall', await getHall(localHall.roomId)]

  const [hall, user] = await Promise.all([
    getHallByIdentifier(identifier),
    getUserByUsername(identifier),
  ])
  if (user) return ['user', user]
  if (hall) return ['hall', hall]
  return undefined
}

export const isRoomMookeb = async (roomId: string): Promise<boolean> => {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return false

  const hall = await getHall(roomId)
  return Boolean(hall?.tags?.includes(MOOKEB_TAG))
}

export const setHallUserSessionsLimit = async (
  roomId: string,
  limit: number,
): Promise<boolean> => {
  const hall = await getHall(roomId)
  if (!hall) return false
  const currentTags = hall.tags || []

  const filteredTags = currentTags.filter(
    tag => !tag.startsWith(USER_SESSIONS_LIMIT_TAG_PREFIX),
  )

  // Add new tag with limit if limit is not 0 (unlimited)
  const newTags =
    limit > 0 ? [...filteredTags, getUserSessionsLimitTag(limit)] : filteredTags

  // Update hall info with new tags
  return await changeHallInfo(roomId, { tags: newTags })
}

export async function getUserSessionsLimitFromHallTags(tags: string[] = []) {
  return getUserSessionsLimitFromTag(
    tags.find(tag => tag?.startsWith(USER_SESSIONS_LIMIT_TAG_PREFIX)),
  )
}

export async function getUserSessionsLimit(roomId: string) {
  const room = await getRoom(roomId)
  if (room?.type !== 'CHANNEL') return 0

  const hall = await getHall(roomId)
  return getUserSessionsLimitFromHallTags(hall?.tags)
}

export async function checkClientUserAccessToHallBasedOnSessionsLimit(
  roomId: string,
): Promise<boolean> {
  const limit = await getUserSessionsLimit(roomId)
  if (limit === 0) return true

  const permissions = await decodeSubscriptionPermissions(roomId)
  if (permissions?.isAdmin) return true

  const activeSessions = await getActiveSessions()

  const others = activeSessions?.others ?? []
  return others.length < limit
}
